import { ComponentType, KeyboardEvent, ReactNode, useRef } from "react";
import cls from "classnames";

import styles from "./index.module.scss";

export type TabItem = {
  value: string;
  label: ReactNode;
  Icon?: ComponentType<{ className?: string }>;
};

type Props = {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  "aria-label"?: string;
  /** When set, tabs get stable ids (`{idBase}-tab-{value}`) and point
   *  aria-controls at `{idBase}-panel-{value}` — give panels that id +
   *  role="tabpanel" + aria-labelledby of the tab id. */
  idBase?: string;
};

/**
 * Underline tab bar (WAI-ARIA tabs, automatic activation): arrow keys move
 * focus AND selection, Home/End jump, roving tabindex keeps a single tab stop.
 */
export default function Tabs({
  items,
  value,
  onChange,
  className,
  idBase,
  ...rest
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);

  const selectAt = (index: number) => {
    const item = items[(index + items.length) % items.length];
    onChange(item.value);
    const tabs = listRef.current?.querySelectorAll<HTMLElement>("[role='tab']");
    tabs?.[(index + items.length) % items.length]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const handlers: { [key: string]: () => void } = {
      ArrowRight: () => selectAt(index + 1),
      ArrowLeft: () => selectAt(index - 1),
      Home: () => selectAt(0),
      End: () => selectAt(items.length - 1),
    };
    const handler = handlers[event.key];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- index read: the key may be absent
    if (handler) {
      event.preventDefault();
      handler();
    }
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={rest["aria-label"]}
      className={cls(styles.tablist, "talaera-tabs", className)}
    >
      {items.map((item, index) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            id={idBase ? `${idBase}-tab-${item.value}` : undefined}
            aria-controls={idBase ? `${idBase}-panel-${item.value}` : undefined}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={cls(styles.tab, "talaera-tab", selected && styles.selected)}
            onClick={() => onChange(item.value)}
            onKeyDown={event => onKeyDown(event, index)}
          >
            {item.Icon && <item.Icon className={styles.icon} />}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
